/**
 * Google gtag.js helpers
 * https://developers.google.com/gtagjs
 */

declare global {
  namespace NodeJS {
    interface ProcessEnv {
      readonly REACT_APP_GA_TOKEN: string;
    }
  }
}

//#region init gtag
declare global {
  interface Window {
    dataLayer: any[];
  }
}
window.dataLayer = window.dataLayer || [];
if (!window.gtag) {
  window.gtag = function gtag() {
    window.dataLayer.push(arguments);
  };
}
window.gtag('js', new Date());

//#endregion

//#region gtag https://developers.google.com/gtagjs/reference/event
const FD_TRACKER = process.env.REACT_APP_GA_TOKEN;

type ConfigOption = [string, Gtag.CustomParams?];
export function initialize(extraTrackers: ConfigOption[], disable = false) {
  [[FD_TRACKER], ...extraTrackers]
    .filter((tid) => !!tid)
    .forEach(([trackingId, options = {}]) => {
      // disable or reset tracking
      (disable ? disableGaTracker : enableGaTracker)(trackingId);

      window.gtag('config', trackingId, {
        cookie_expires: 360 * 24 * 60 * 60, // need set expiration for all trackers
        cookie_update: true,
        cookie_flags: 'SameSite=None; Secure',
        send_page_view: false, // manually send page_view
        ...(options as ConfigOption[1]),
      });
    });

  window.gtag('set', {
    hostname: window.location.hostname,
  });

  if (
    /* in case Elixir loads one (or two) */
    !document.querySelector(
      'script[src^="https://www.googletagmanager.com/gtag"]',
    )
  ) {
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${FD_TRACKER}`;
    document.head.insertBefore(script, document.head.firstChild);
  }
}

// @example how to callback from gtag
// WARNING, probably u need to do some kind of timeout outside in case gtag is disabled
// window.gtag('get', <tracker_id>, 'client_id', (x) => {
//   debugger;
//   console.log('XXX', x);
// });
// OR
// window.dataLayer.push(function() {
//   const existingTime = this.get('time');
//   debugger;
//   console.log('XXX', this);
// });
// OR
// new Promise(resolve => {
//  gtag('event', 'transaction', { ..., 'event_callback': resolve }});
// })

/**
 * Disable Google Analytics measurement
 * https://developers.google.com/analytics/devguides/collection/gtagjs/user-opt-out
 */
function disableGaTracker(trackerId: string) {
  window[`ga-disable-${trackerId}`] = true;
}
function enableGaTracker(trackerId: string) {
  delete window[`ga-disable-${trackerId}`];
}

/**
 *  You can measure exceptions to monitor the number and type of crashes or errors that occur on a web page
 * @example
 * gtag('event', 'exception', {
      'description': 'error_description',
      'fatal': false   // set to true if the error is fatal
    });
 */
export function exception(description: string, fatal = false) {
  window.gtag('event', 'exception', {
    description,
    fatal,
  });
}

export function pageAndScreenView(title: string) {
  window.gtag('set', {
    app_name: 'flipdish',
    page_title: document.title,
    page_location: window.location.href,
    page_path: window.location.pathname,
    screen_name: title,
  });
  window.gtag('event', 'page_view', {});
}

export function event(
  category: string,
  action: string,
  props: Gtag.EventParams,
) {
  window.gtag('event', action, {
    event_category: category,
    ...props,
  });
}

//#endregion

//#region eCOMMERCE https://developers.google.com/analytics/devguides/collection/gtagjs/enhanced-ecommerce

type ItemProp = Required<
  Pick<Gtag.Item, 'id' | 'category' | 'name' | 'quantity' | 'price'>
> &
  Gtag.Item;
/**
 * Measure additions to shopping carts
 */
function ecAddToCart(items: ItemProp[]) {
  window.gtag('event', 'add_to_cart', {
    items: items.map((item) => ({
      ...item,
      category: 'flipdish',
    })),
  });
}
/**
 * Measure removals from shopping carts
 */
function ecRemoveFromCart(items: ItemProp[]) {
  window.gtag('event', 'remove_from_cart', {
    items: items.map((item) => ({
      ...item,
      category: 'flipdish',
    })),
  });
}

/**
 * To measure the first checkout step
 * @example
 * gtag('event', 'begin_checkout', {
    "items": [
      {
        "id": "P12345",
        "name": "Android Warhol T-Shirt",
        "list_name": "Search Results",
        "brand": "Google",
        "category": "Apparel/T-Shirts",
        "variant": "Black",
        "list_position": 1,
        "quantity": 2,
        "price": '2.0'
      }
    ],
    "coupon": ""
});

 */
function ecCheckoutStart(props: Gtag.EventParams) {
  window.gtag('event', 'begin_checkout', props);
}
/**
 * To measure each subsequent checkout step
 * @example
 * gtag('event', 'checkout_progress', {
    "items": [
      {
        "id": "P12345",
        "name": "Android Warhol T-Shirt",
        "list_name": "Search Results",
        "brand": "Google",
        "category": "Apparel/T-Shirts",
        "variant": "Black",
        "list_position": 1,
        "quantity": 2,
        "price": '2.0'
      }
    ],
    "coupon": "SUMMER_DISCOUNT"
});

 */
function ecCheckoutUpdate(props: Gtag.EventParams) {
  window.gtag('event', 'checkout_progress', props);
}

type PurchaseProps = Required<
  Pick<Gtag.EventParams, 'transaction_id' | 'value' | 'currency' | 'items'>
> &
  Gtag.EventParams & { affiliation?: string };
/**
 * Measure purchase (transaction)
 * @example
 * gtag('event', 'purchase', {
  "transaction_id": "24.031608523954162",
  "affiliation": "Google online store",
  "value": 23.07,
  "currency": "USD",
  "tax": 1.24,
  "shipping": 0,
  "items": [
    {
      "id": "P12345",
      "name": "Android Warhol T-Shirt",
      "list_name": "Search Results",
      "brand": "Google",
      "category": "Apparel/T-Shirts",
      "variant": "Black",
      "list_position": 1,
      "quantity": 2,
      "price": '2.0'
    },
    {
      "id": "P67890",
      "name": "Flame challenge TShirt",
      "list_name": "Search Results",
      "brand": "MyBrand",
      "category": "Apparel/T-Shirts",
      "variant": "Red",
      "list_position": 2,
      "quantity": 1,
      "price": '3.0'
    }
  ]
});
 */
function ecPurchase(props: PurchaseProps) {
  window.gtag('event', 'purchase', {
    affiliation: 'Flipdish - order',
    revenue: props.value,
    items: props.items.map((item) => ({
      ...item,
      category: 'flipdish',
    })),
    ...props,
  });
}

type RefundProps = Required<Pick<Gtag.EventParams, 'transaction_id'>> &
  Gtag.EventParams;
/**
 * Measure refunds
 * @example
 * // Full refund
 * gtag('event', 'refund', { "transaction_id": "T12345" })
 * 
 * // Partial refund, send a refund event with the transaction ID and the items to be refunded:
 * gtag('event', 'refund', {
    "transaction_id": "79.18502354114992",
    "affiliation": "Google online store",
    "value": 23.07,
    "currency": "USD",
    "tax": 1.24,
    "shipping": 0,
    "items": [
      {...}
    ]
  });
 */
function ecRefund(props: RefundProps) {
  window.gtag('event', 'refund', props);
}

export const eCommerce = {
  addToCart: ecAddToCart,
  removeFromCart: ecRemoveFromCart,
  checkoutStart: ecCheckoutStart,
  checkoutUpdate: ecCheckoutUpdate,
  purchase: ecPurchase,
  refund: ecRefund,
};
//#endregion
